/**
 * # dxfBridge.mleaderContextConversion
 *
 * Model→transport MULTILEADER CONTEXT_DATA + leader-hierarchy converters,
 * split out of `dxfBridge.entityConversion.ts` (which is pinned at the
 * CLAUDE.md 2000-line guardrail) — same sibling-encoder precedent as
 * `dxfBridge.attribExportPosition.ts` / `dxfBridge.ellipse.ts` /
 * `dxfBridge.spline.ts`. The sole external call site
 * (`dxfBridge.entityConversion.ts`'s `"Multileader"` case) imports these back;
 * the symmetric reverse-direction (DXF → model) converter lives in
 * `dxfBridge.drawingToDxf.ts`.
 *
 * ── S-MLEADER-CONTEXT-STRUCTURAL (sub-slice D) — model→transport CONTEXT_DATA ──
 *
 * The drawing-model `contextData` (mleaderContextSchema) and the dxf-io
 * transport (DxfMLeaderContext) carry IDENTICAL field names, and the model's
 * 3D points (drawingPoint3DSchema → {x,y,z}) already match DxfPoint3D, so a
 * structural spread carries every header + INLINE MTextData / BlockData scalar
 * + point verbatim into the transport the byte-faithful writer
 * (mleaderContextWriter) re-emits. The symmetric import lives in
 * dxfBridge.drawingToDxf.ts (`toDrawingEntity`).
 */

import {
  type DxfMLeaderBreakPoint,
  type DxfMLeaderContext,
  type DxfMLeaderEntity,
  type DxfMLeaderLeaderLine,
  type DxfMLeaderLeaderNode,
  type DxfPoint3D,
} from "@dxf-ai-editor/dxf-io";

import { type MultileaderEntity } from "./model";

// `toDxfPoint3D` is intentionally NOT imported from `dxfBridge.entityConversion
// .ts` — that file imports `buildAuthoredMLeaderContext` /
// `modelMLeaderContextToDxf` / `modelMLeaderSecondaryContextsToDxf` /
// `modelMLeaderLeaderRootsToDxf` back FROM this file (its sole external call
// site), so a reverse import would close an import cycle (forbidden by the
// dependency-cruiser zero-cycle gate). A 3-line pure default-z helper
// duplicated across this split boundary already has precedent in this exact
// codebase (`dxfBridge.attribExportPosition.ts`'s own header documents the
// identical `usesAlignmentPoint` cross-package duplication as "incidental,
// not a semantic clone to extract").
function toDxfPoint3D(point: { x: number; y: number; z?: number }): DxfPoint3D {
  return { x: point.x, y: point.y, z: point.z ?? 0 };
}

// GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the SAME
// epsilon-tolerant "is this OCS normal left-handed (mirrored)?" predicate
// `dxfBridge.drawingToDxf.ts`'s import-side `isMLeaderMTextMirrored` uses
// (re-exported from here so both directions share ONE threshold — an
// untouched source extrusion with a near-zero-but-negative Z, e.g.
// `z=-1e-12`, must classify the SAME way on import AND on export, or a
// no-edit round-trip spuriously "detects" a divergence and deletes the
// source extrusion). Same rationale/threshold as `command-engine`'s
// `isReflectedOcs` (not importable here — package-boundary cycle, see the
// `toDxfPoint3D` doc above).
const MLEADER_MIRROR_Z_EPS = 1e-9;
// NOTE — tracked as https://github.com/cosc/Edit-dwg/issues/564
// ("MLeader in-plane MTEXT extrusion loses textMirrored across
// save/reopen round-trip") — see that issue for the full root-cause
// writeup, an authoritative ezdxf citation confirming even the reference
// implementation declines to solve the general case, and a completion
// plan. Short version: this Z-sign-only classification is EXACT for the
// canonical axis-aligned case and for ANY genuinely tilted extrusion with
// a nonzero Z (a single reflection always flips a nonzero Z's sign — see
// `reflectExtrusion`/`reflectVectorXY`, `geometryTransforms.ts`, which
// leaves Z unchanged before negating all three components — so Z-sign
// correctly round-trips post-mirror for any `z != 0`). It is undefined
// ONLY for an extrusion whose Z is exactly (or within `MLEADER_MIRROR_Z
// _EPS` of) zero — an in-plane normal has no Z sign to flip, so a MIRROR
// + save + reopen round-trip re-derives the SAME (pre-mirror) boolean on
// reimport, silently reverting the glyph handedness. Narrow in practice
// (an in-plane MTEXT extrusion means the text plane sits exactly
// perpendicular to the WCS XY-plane — an unusual 3D-drafting scenario,
// not an ordinary 2D drawing's default `{0,0,1}`).
export function isMLeaderMTextMirroredExtrusion(
  extrusion: { z?: number } | undefined,
): boolean {
  return (extrusion?.z ?? 0) < -MLEADER_MIRROR_Z_EPS;
}

/** Model→transport MLEADER break point: fills the model's optional z (the
 * transport carries a full DxfPoint3D) and carries the optional LeaderLine
 * break index (group 90). */
function modelMLeaderBreakPointToDxf(bp: {
  start: { x: number; y: number; z?: number };
  end: { x: number; y: number; z?: number };
  breakIndex?: number;
}): DxfMLeaderBreakPoint {
  const out: DxfMLeaderBreakPoint = {
    start: toDxfPoint3D(bp.start),
    end: toDxfPoint3D(bp.end),
  };
  if (bp.breakIndex !== undefined) out.breakIndex = bp.breakIndex;
  return out;
}

/** Model→transport CONTEXT_DATA header (+ INLINE MTextData / BlockData).
 *
 * The UI-facing MTEXT fields (`textContents`, `textLocation`, `textRotation`,
 * `textHeight`) are projected INTO the copied MTextData / context header so C's
 * byte-faithful writer re-emits the CURRENT values, not the stale parsed source
 * (Codex #270). Without this an edited MLEADER (rename text, MOVE the text
 * insert, rotate, resize text height) exports the original `defaultContent` /
 * `insert` / rotation / `charHeight` because the writer suppresses the flat body
 * fields when a context is present and emits from the context tree.
 *
 * Each projection is CONDITIONAL on the value actually DIVERGING from the
 * source (an edit) so a sparse / unchanged context is NOT re-materialized with
 * invented groups (byte-faithfulness, the sibling Codex #270 absence-preserve
 * fix). `textLocation` is projected only when the source already carried an
 * `insert` (group 12/22/32) — otherwise the flat-body emit owns it — and keeps
 * the source z. Points spread verbatim ({x,y,z}). */
export function modelMLeaderContextToDxf(
  ctx: NonNullable<MultileaderEntity["contextData"]>,
  edits: {
    textContents: string;
    textLocation: { x: number; y: number };
    textRotation: number;
    textMirrored: boolean;
    textHeight: number;
    landingGap: number;
    textLineSpacingStyle: number;
    textLineSpacingFactor: number;
    // GitHub Codex review PR #558 round 39 (formal bot review, HEAD
    // 6bf071e6) P2 — "Allow MText materialization alongside cached block
    // data". The entity-level DXF group 172 content type — the ONLY
    // authoritative signal for which context content is ACTIVE. `ctx
    // .hasBlock`/`ctx.blockData` being present does NOT mean Block content
    // is active: real AutoCAD (and this codebase, since round 34 of this
    // same PR — `geometryReducerEdit.ts`'s `isMTextContentType` gate)
    // preserves CACHED block/MText data in CONTEXT_DATA independently of
    // which content type is currently selected, so a genuinely
    // MText-active MLeader (`contentType === 2`) can still carry stale
    // cached `blockData` from an earlier content-type toggle.
    // `needsMaterializedSpacingMText` below uses THIS field, not cached
    // data presence, to decide whether materializing a minimal MTextData
    // is legitimate.
    contentType: number;
  },
): DxfMLeaderContext {
  let mTextData: DxfMLeaderContext["mTextData"];
  if (ctx.mTextData) {
    mTextData = { ...ctx.mTextData };
    // 42 — text rotation. Real ezdxf home (AcDbMLeader body group 42 is
    // arrow_head_size). Project only on divergence from the source.
    if (edits.textRotation !== (mTextData.rotation ?? 0)) {
      mTextData.rotation = edits.textRotation;
    }
    // GitHub Codex review PR #558 P2, SUPERSEDED by the round-17 + round-20
    // local read-only follow-up P2 ("Preserve the primary MLeader extrusion
    // when mirroring" / "Preserve in-plane MLeader extrusions during
    // export") — 11/21/31 extrusion, the handedness companion to rotation
    // above (real ezdxf home: `mleaderMTextDataSchema.extrusion`'s own
    // doc). This FIRST draft re-derived "is it mirrored" from
    // `mTextData.extrusion`'s OWN Z-sign (`isMLeaderMTextMirroredExtrusion`)
    // and, on divergence from `edits.textMirrored`, OVERWROTE the extrusion
    // with the canonical axis-aligned `{0,0,-1}` pair (or deleted it) —
    // discarding a genuinely tilted (non-axis-aligned) source normal.
    //
    // Round-17's fix moved the REAL reflection into the MIRROR reducer
    // (`geometryReducerEdit.ts`, which has the mirror axis this package
    // cannot import `reflectExtrusion` to compute) — the reducer now
    // updates `entity.contextData.mTextData.extrusion` AND
    // `entity.textMirrored` atomically, in the SAME operation, so `ctx`
    // (this function's own `entity.contextData`) is ALWAYS geometrically
    // consistent with `edits.textMirrored` by construction, whether that
    // consistency comes from IMPORT (`entity.textMirrored` is ITSELF
    // derived from `isMLeaderMTextMirroredExtrusion(ctx.mTextData
    // .extrusion)` at parse time — `dxfBridge.drawingToDxf.ts`) or from a
    // subsequent MIRROR (the reducer keeps both fields in lockstep).
    // `entity.textMirrored` has NO OTHER writer in this codebase.
    //
    // Round-20's finding: the divergence check above is not just
    // redundant but ACTIVELY WRONG for an in-plane (Z=0) tilted source
    // (e.g. `{0.6, 0.8, 0}`) — `isMLeaderMTextMirroredExtrusion`'s Z-sign
    // -only classification reads BOTH the unreflected AND the reducer-
    // reflected vector as "not mirrored" (neither has a negative Z), so
    // this branch would spuriously detect a "divergence" from
    // `edits.textMirrored === true` and OVERWRITE the reducer's already-
    // correct reflection with the canonical `{0,0,-1}` — silently undoing
    // round-17's own fix for exactly the tilt shape it was meant to
    // preserve. `mTextData = { ...ctx.mTextData }` above ALREADY carries
    // the (now always-correct) extrusion through verbatim — no further
    // derivation needed.
    // SL-5 P2-4 — line spacing. MLEADER line-spacing's authoritative DXF home is
    // CONTEXT_DATA MTextData 170 (style) / 45 (factor); the body 48/49 emit was
    // removed in P3c, so the flat textLineSpacingStyle/Factor was IGNORED on the
    // faithful export path — an edit to line-spacing re-emitted the STALE source
    // 45/170, and a legacy Edit-DWG file whose spacing lived only in body 48/49
    // lost it. Project the (possibly edited) flat field onto the mTextData ONLY
    // on DIVERGENCE from the source value (the same divergence-gated pattern as
    // rotation/contents/charHeight/landingGap): an unchanged value stays
    // byte-faithful (no invented group), an edit or a migrated legacy flat value
    // materialises into the correct 45/170 home. The mTextData defaults for both
    // are 1 (ezdxf MTextData line_spacing_style / line_spacing_factor).
    if (edits.textLineSpacingStyle !== (mTextData.lineSpacingStyle ?? 1)) {
      mTextData.lineSpacingStyle = edits.textLineSpacingStyle;
    }
    if (edits.textLineSpacingFactor !== (mTextData.lineSpacingFactor ?? 1)) {
      mTextData.lineSpacingFactor = edits.textLineSpacingFactor;
    }
    // 304 — MTEXT contents. Adds on edit-from-empty, updates on edit, no-op
    // (faithful) when unchanged; absent + empty model text → never invented.
    if (edits.textContents !== (mTextData.defaultContent ?? "")) {
      mTextData.defaultContent = edits.textContents;
    }
    // 12/22/32 — insertion point. Only when the source carried an insert AND
    // the x/y changed (e.g. a MOVE updated the entity textLocation but the
    // typed insert went stale). Preserve the source z (the model point is 2D).
    const insert = mTextData.insert;
    if (
      insert &&
      (insert.x !== edits.textLocation.x || insert.y !== edits.textLocation.y)
    ) {
      mTextData.insert = {
        x: edits.textLocation.x,
        y: edits.textLocation.y,
        z: insert.z,
      };
    }
  }
  // SL-5 F3 (@codex round-3) — MATERIALIZE a minimal MTextData for migrated legacy
  // line-spacing that has NO source MTextData. SL-5 F5 promotes a legacy Edit-DWG
  // body 48/49 (line_spacing_style/factor) into the flat textLineSpacingStyle/
  // Factor, and P3c removed the body 48/49 emit, so the ONLY DXF home left is the
  // CONTEXT_DATA MTextData 45/170 — but the projection above runs INSIDE
  // `if (ctx.mTextData)`. A legacy CONTEXT_DATA with `290 0` (no MTextData) carries
  // the migrated flat spacing yet no mTextData container, so the 45/170 were never
  // written and the body 48/49 are gone → open/save DROPPED the spacing. When the
  // spacing is NON-default (style≠1 or factor≠1) and there is no source MTextData
  // AND the MLeader's ACTIVE content type is MText (line-spacing is MTEXT-only;
  // never graft an MTextData onto a context whose ACTIVE content is Block),
  // materialise a MINIMAL MTextData carrying only the 45/170
  // (ezdxf 1.4.3 `MTextData.parse` consumes any acdb_mleader MTextData ATTRIBS
  // code, so a 45/170-only block is valid — every other field defaults) and flip
  // `hasMText` so the byte-faithful writer emits `290 1` + the block. Default
  // spacing (1/1) is left absent — never invent a spurious MTextData (the task's
  // "no spacing to preserve" guard). Only touch `hasMText` in THIS materialised
  // case so an existing-MTextData / block context stays byte-faithful.
  // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — a MIRRTEXT=1
  // edit on a legacy (no source MTextData) context needs this SAME
  // materialization path, or the rotation projection above (which only
  // runs `if (ctx.mTextData)`) never fires and the new handedness is
  // silently dropped on export. `edits.textMirrored` joins the trigger
  // condition alongside the pre-existing line-spacing arm.
  //
  // GitHub Codex review PR #558 round 2 P2 — `edits.textRotation !== 0`
  // ALSO joins the trigger (not just `textMirrored`): MLEADER rotation has
  // NO body-level DXF fallback (real ezdxf home is CONTEXT_DATA MTextData
  // group 42 ONLY — see the `ctx.mTextData` branch's own doc), so a
  // MIRRTEXT=0 (readable) mirror on a legacy/context-sparse entity — which
  // changes ONLY the rotation, never `textMirrored` — previously fell
  // through this materialization gate entirely, saving no group 42;
  // reopening reset the angle to 0 while a MIRRTEXT=1 edit's mirrored
  // EXTRUSION was still (wrongly, independently) preserved.
  //
  // GitHub Codex review PR #558 round 39 (formal bot review, HEAD
  // 6bf071e6) P2 — "Allow MText materialization alongside cached block
  // data". The `!ctx.hasBlock && !ctx.blockData` gate this branch used
  // through round 38 conflated "cached block data is PRESENT" with "Block
  // content is ACTIVE" — the two are independent (see `edits.contentType`'s
  // own doc above this function). An imported MLeader whose ACTIVE content
  // is MText (`contentType === 2`) but whose context ALSO carries cached
  // `blockData`/`hasBlock` from an earlier content-type toggle previously
  // never materialized here, so a MIRRTEXT edit's rotation/handedness was
  // silently dropped on export for exactly that (valid, real-world) cached
  // state. Replaced with `isMTextContentType`, the authoritative signal —
  // Block-active contexts (`contentType !== 2`) still correctly never get
  // an MText context grafted on, matching the ORIGINAL "never graft onto a
  // Block context" intent, just keyed on the right field.
  const isMTextContentType = edits.contentType === 2;
  const needsMaterializedSpacingMText =
    !ctx.mTextData &&
    isMTextContentType &&
    (edits.textLineSpacingStyle !== 1 ||
      edits.textLineSpacingFactor !== 1 ||
      edits.textMirrored ||
      edits.textRotation !== 0);
  if (needsMaterializedSpacingMText) {
    mTextData = {
      // GitHub Codex review PR #558 round 2 (round-15 local read-only
      // follow-up) P2 — "Omit default spacing from rotation-only
      // MTextData". This materialization branch's OWN trigger condition
      // ORs FOUR independent divergences together (spacing style/factor,
      // textMirrored, textRotation) — a mirror/rotation-ONLY edit on a
      // sparse context (default 1/1 spacing) previously still
      // UNCONDITIONALLY assigned both spacing fields, emitting spurious
      // DXF groups 170/45 with their OWN implied-default values (1) even
      // though the source never had them. Divergence-gate BOTH spacing
      // fields the SAME way `rotation`/`extrusion` already are two lines
      // below — matching the `ctx.mTextData` branch's own divergence
      // convention for the identical two fields (lines ~156-161 above).
      ...(edits.textLineSpacingStyle !== 1
        ? { lineSpacingStyle: edits.textLineSpacingStyle }
        : {}),
      ...(edits.textLineSpacingFactor !== 1
        ? { lineSpacingFactor: edits.textLineSpacingFactor }
        : {}),
      ...(edits.textRotation !== 0 ? { rotation: edits.textRotation } : {}),
      ...(edits.textMirrored ? { extrusion: { x: 0, y: 0, z: -1 } } : {}),
    };
  }
  const out: DxfMLeaderContext = {
    ...ctx,
    mTextData,
    blockData: ctx.blockData ? { ...ctx.blockData } : undefined,
  };
  // Flip has_mtext (290) so the writer actually emits the materialised block
  // (`emitContextHeader` gates the MTextData emit on `hasMText && mTextData`).
  if (needsMaterializedSpacingMText) out.hasMText = true;
  // 41 — context char_height. The MLEADER text height's authoritative DXF home
  // in a context-bearing MLEADER is CONTEXT_DATA group 41 (charHeight), NOT body
  // 40 (the byte-faithful writer suppresses body 40 when charHeight is present),
  // so an edited text height was silently dropped — it exported the original
  // charHeight (Codex #270 textHeight projection arm; Q5 shipped the contents/
  // rotation/location arms but missed this). Project the (possibly edited)
  // entity.textHeight onto charHeight ONLY when the source already carried a
  // charHeight AND the value diverges (an edit) — same divergence-gated pattern
  // as the rotation/contents arms. Never INVENT a charHeight on a source that had
  // none: the body-40 channel owns that case (and carries an edit via group 40),
  // so projecting here would fabricate a group 41 the source never had.
  if (ctx.charHeight !== undefined && edits.textHeight !== ctx.charHeight) {
    out.charHeight = edits.textHeight;
  }
  // 145 — landing gap size. In a context-bearing MLEADER the landing gap's
  // authoritative DXF home is CONTEXT_DATA group 145 (landingGapSize): the
  // byte-faithful writer emits 145 from contextData.landingGapSize and IGNORES
  // the body-level geom.landingGap on this faithful path. So an edited landing
  // gap on an imported context-backed MLEADER was silently dropped — export kept
  // the original 145 and re-import returned the stale gap (Codex #285-7). Project
  // the (possibly edited) entity.landingGap onto landingGapSize on DIVERGENCE
  // from the value the import derived — the same divergence-gated pattern as the
  // charHeight arm above, with BOTH source arms (#294 Codex P2, absent-145 arm):
  //   • source carried 145 → import baseline is that value; project on edit.
  //   • source had NO 145 → import baseline is the default 2 (the model derives
  //     landingGap from `landingGapSize ?? landingGap ?? 2`, and the parser
  //     never sets a flat landingGap — gap-C.2 removed the bogus body 44, so a
  //     context without 145 always lands on 2). Unlike charHeight (whose
  //     absent-41 edits ride the body-40 channel) the landing gap has NO body
  //     channel on the faithful path, so an edit MUST materialize 145 here or
  //     it is lost ("source had no 145, user sets gap to 7").
  // An UNEDITED absent 145 stays absent (edits.landingGap === 2 → no
  // divergence → nothing invented; preserve-source-never-infer). An edit BACK
  // to exactly 2 on an absent-145 source is indistinguishable from no-edit at
  // this layer and stays absent — value-faithful (re-import derives 2 again).
  const sourceLandingGap = ctx.landingGapSize ?? 2;
  if (edits.landingGap !== sourceLandingGap) {
    out.landingGapSize = edits.landingGap;
  }
  return out;
}

/**
 * SL-5 P1 — synthesize a COMPLETE CONTEXT_DATA header for an AUTHORED MLEADER
 * (one that carries no parsed `contextData`, e.g. created via
 * createMultileaderEntity). Without this the writer took the marker-less fallback
 * (`buildMLeaderContextGroups` else-branch) which emitted only the leaders,
 * OMITTING the mandatory ezdxf `MLeaderContext.export_dxf` header — every
 * model-authored MLEADER was structurally malformed (no 40/10-30/41/140/145/
 * 174-177/290/296/110-112/297).
 *
 * Field-for-field against ezdxf 1.4.3 `MLeaderContext.__init__` defaults (scale
 * 1, char_height/arrow_head_size 4, landing_gap 2, left/right_attachment 1,
 * text_align_type 0, attachment_type 0, plane origin NULLVEC / X_AXIS / Y_AXIS,
 * plane_normal_reversed 0, top/bottom_attachment 9). AutoCAD-parity values come
 * from the model entity (charHeight ← textHeight, arrow_head_size ←
 * arrowheadSize, landing_gap ← landingGap, base_point ← textLocation).
 *
 * has_mtext (290) / has_block (296) are BOTH set (mandatory pair). When the
 * content is MTEXT (contentType 2) an INLINE MTextData carries the authored text
 * (304), insert (12/22/32), rotation (42) and — SL-5 P3c — the line-spacing
 * (45 factor / 170 style, the correct ezdxf home; NOT the removed body 48/49).
 * The text-style handle (MTextData 340) is left for the document orchestrator
 * (`stampMLeaderTextStyleHandles`) to resolve from textStyleName.
 */
export function buildAuthoredMLeaderContext(
  entity: MultileaderEntity,
): DxfMLeaderContext {
  const base = toDxfPoint3D(entity.textLocation);
  const isMText = entity.contentType === 2;
  const isBlock = entity.contentType === 1;
  // A conformant CONTEXT_DATA BlockData (ezdxf `BlockData.export_dxf`) REQUIRES a
  // 341 block_record_handle + a 15/25/35 insertion; a `296 1` header without them
  // is malformed. An AUTHORED (non-imported) block-content MLEADER cannot yet
  // carry a resolvable block: `geometry/create-multileader` derives ONLY
  // contentType from the style, `createMultileaderEntity` defaults to MText, and
  // no property/command path attaches a block to the entity (propertyCapabilities
  // keeps the MLEADER content/block graph out of generic edit) — so
  // blockName/blockRecordHandle stay empty on a fresh block-content MLEADER.
  // Therefore emit block content ONLY when the entity actually carries a block
  // record handle (a future block-attach author, or a re-export of authored
  // round-trip data); otherwise DO NOT emit `296 1`/BlockData at all
  // (hasBlock=false) so we never write a malformed partial BlockData
  // (ПРИОРИТЕТ #1 — no placeholder). Authored block-content authoring is a scoped
  // future item: docs/test-gaps/mleader-authored-block-content.md.
  const blockRecordHandle = entity.blockRecordHandle;
  const hasBlockContent =
    isBlock && blockRecordHandle !== undefined && blockRecordHandle !== "";
  const ctx: DxfMLeaderContext = {
    scale: 1,
    basePoint: base,
    charHeight: entity.textHeight,
    arrowHeadSize: entity.arrowheadSize,
    landingGapSize: entity.landingGap,
    textLeftAttachment: 1,
    textRightAttachment: 1,
    textAlignType: 0,
    attachmentType: 0,
    hasMText: isMText,
    hasBlock: hasBlockContent,
    planeOrigin: { x: 0, y: 0, z: 0 },
    planeXAxis: { x: 1, y: 0, z: 0 },
    planeYAxis: { x: 0, y: 1, z: 0 },
    planeNormalReversed: false,
    topAttachment: 9,
    bottomAttachment: 9,
  };
  if (isMText) {
    ctx.mTextData = {
      defaultContent: entity.textContents,
      insert: base,
      ...(entity.textRotation ? { rotation: entity.textRotation } : {}),
      // GitHub Codex review PR #558 P2 — same authored-fallback pattern as
      // `rotation` immediately above, for the handedness companion (real
      // ezdxf home: MTextData extrusion group 11/21/31).
      ...(entity.textMirrored ? { extrusion: { x: 0, y: 0, z: -1 } } : {}),
      lineSpacingFactor: entity.textLineSpacingFactor,
      lineSpacingStyle: entity.textLineSpacingStyle,
    };
  }
  if (hasBlockContent) {
    // COMPLETE ezdxf BlockData (`BlockData.export_dxf` order): 341 block record
    // handle + 14/24/34 extrusion + 15/25/35 insert + 16/26/36 scale + 46
    // rotation, so a `296 1` header always carries a valid block reference and
    // insertion point (never the prior 341-less/insert-less partial record).
    ctx.blockData = {
      blockRecordHandle,
      extrusion: { x: 0, y: 0, z: 1 },
      insert: base,
      scale: { x: entity.blockScale, y: entity.blockScale, z: entity.blockScale },
      rotation: entity.blockRotation,
    };
  }
  return ctx;
}

/** Model→transport SECONDARY CONTEXT_DATA blocks (#294 Codex P2). Each
 * annotative secondary block re-emits VERBATIM — context header (+ INLINE
 * MTextData / BlockData) copied as-is, leader hierarchy through the shared
 * root converter, per-block raw remainder untouched. Unlike the PRIMARY
 * (`modelMLeaderContextToDxf`), NO entity-level UI edits are projected: the
 * flat entity fields (textContents / textLocation / textRotation / textHeight
 * / landingGap) describe the primary context; stamping them onto every
 * per-scale secondary would corrupt the per-scale values AutoCAD stores there
 * (preserve-source-never-infer). The copies (not references) keep the export
 * orchestrator's handle restamps off the model entity. */
export function modelMLeaderSecondaryContextsToDxf(
  secondaries: NonNullable<MultileaderEntity["secondaryContexts"]>,
): NonNullable<DxfMLeaderEntity["geometry"]["secondaryContexts"]> {
  return secondaries.map((sec) => {
    const entry: NonNullable<
      DxfMLeaderEntity["geometry"]["secondaryContexts"]
    >[number] = {
      context: {
        ...sec.context,
        mTextData: sec.context.mTextData
          ? { ...sec.context.mTextData }
          : undefined,
        blockData: sec.context.blockData
          ? { ...sec.context.blockData }
          : undefined,
      },
      leaderRoots: modelMLeaderLeaderRootsToDxf(sec.leaderRoots),
    };
    if (sec.rawContextGroups && sec.rawContextGroups.length > 0) {
      entry.rawContextGroups = sec.rawContextGroups.map((grp) => ({
        code: grp.code,
        value: grp.value,
      }));
    }
    return entry;
  });
}

/** Model→transport leader hierarchy. Carries the per-LEADER (node) typed fields
 * — lastLeaderPoint / dogleg / index / attachment direction / breaks — and the
 * per-LEADER_LINE (line) typed fields — index / color / line-type(+handle) /
 * lineweight override / breaks. 2D vertices and optional-z break points are
 * converted; the 3D node points spread through unchanged. */
export function modelMLeaderLeaderRootsToDxf(
  roots: MultileaderEntity["leaderRoots"],
): DxfMLeaderEntity["geometry"]["leaderRoots"] {
  return roots.map((root) => ({
    nodes: root.nodes.map((node) => {
      const { lines, breakPoints, doglegVector, lastLeaderPoint, ...nodeRest } =
        node;
      const dxfNode: DxfMLeaderLeaderNode = {
        ...nodeRest,
        lines: lines.map((line) => {
          const {
            vertices,
            breakPoints: lineBreaks,
            arrowheadBlock,
            ...lineRest
          } = line;
          const dxfLine: DxfMLeaderLeaderLine = {
            ...lineRest,
            // FIX 3 (sub-slice D follow-up) — emit the leader-line vertex z
            // (group 30). The model vertices are now optional-z (mirroring the
            // sibling breakPoints / node points); toDxfPoint3D fills z ?? 0 so a
            // 2D `{x, y}` literal still emits z=0 while a real z round-trips.
            vertices: vertices.map((v) => toDxfPoint3D(v)),
          };
          // Nullish guard mirrors the sibling LEADER node-path below (~:321).
          // The model line-level `breakPoints` is typed required via the
          // schema `.default([])`, but runtime objects that bypass Zod
          // validation (programmatic store construction, `as` casts, pre-field
          // serialized state) can still carry `undefined` — an unguarded
          // `lineBreaks.length` then throws on export. Absent stays absent
          // (round-trip faithful); present arrays still emit unchanged.
          if (lineBreaks && lineBreaks.length > 0) {
            dxfLine.breakPoints = lineBreaks.map(modelMLeaderBreakPointToDxf);
          }
          if (arrowheadBlock) dxfLine.arrowheadBlock = arrowheadBlock;
          return dxfLine;
        }),
      };
      if (doglegVector) dxfNode.doglegVector = toDxfPoint3D(doglegVector);
      if (lastLeaderPoint) {
        dxfNode.lastLeaderPoint = toDxfPoint3D(lastLeaderPoint);
      }
      if (breakPoints && breakPoints.length > 0) {
        dxfNode.breakPoints = breakPoints.map(modelMLeaderBreakPointToDxf);
      }
      return dxfNode;
    }),
  }));
}
